import { get } from 'radash';
import { useCallback, useEffect, useRef } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/core/Button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogHeaderDefault } from '@/components/core/Dialog';
import { ValidationProvider } from '@/dialogs/item-editor/Context/ValidationContext';
import { useCreateOrUpdateItem, useRemoteValidation } from '@/dialogs/item-editor/ItemEditor.queries';
import useCustomItemEditorSettings from '@/hooks/useCustomItemEditorSettings';
import { useDataModel } from '@/hooks/useDataModel';
import { useDialogWithDirtyCheck } from '@/hooks/useDialogWithDirtyCheck';
import { useIsMd } from '@/hooks/useMediaQuery';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { Item, PersistedItem } from '@/types/DataModel';

import { AttributeComponent } from '../../AttributeComponent';
import { useValidationsStore } from '../../hooks/useValidationsStore';

interface ReferenceItemOption extends PersistedItem {
    key?: string;
    label?: string;
}

interface CreateNewItemDialogProps {
    isOpen: boolean;
    attribute: any;
    onOpenChange: (open: boolean) => void;
    onItemCreated: (item: ReferenceItemOption) => void;
}

export const CreateNewItemDialog = ({
    isOpen,
    attribute,
    onOpenChange,
    onItemCreated,
}: CreateNewItemDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { dataModel } = useDataModel();
    const { getValues } = useFormContext();
    const isMd = useIsMd();

    const {customItemEditorSettings} = useCustomItemEditorSettings();
    const template = customItemEditorSettings?.createHierarchyItemTemplate(getValues(), attribute.name);

    // Keep track of the previous item state for validation
    const previousItemRef = useRef<Item>({ attributeStates__: [] });
    const isValidationInProgressRef = useRef<boolean>(false);
    const lastValidatedValuesRef = useRef<any>(null);
    const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isInitialTemplateValidationRef = useRef<boolean>(false);
    const lastValidatedFieldValuesRef = useRef<Map<string, any>>(new Map());

    // Antipattern: We are tapping into global validations store (shared with parent editor), yet we have a local RHF form for state.
    // If this causes problems in the future, atleast use localValidationErrors from useValidationsStore. Or, a bit overkill, but we can
    // always use local Zustand store held in a React context.
    const { addError, removeErrorByAttributeName, addWarning, removeWarningByAttributeName } = useValidationsStore(useShallow(state => ({
        addError: state.addError,
        removeErrorByAttributeName: state.removeErrorByAttributeName,
        addWarning: state.addWarning,
        removeWarningByAttributeName: state.removeWarningByAttributeName,
    })));

    const methods = useForm({
        defaultValues: template || {}
    });
    const { isDirty } = methods.formState;

    const { mutate: validateItem } = useRemoteValidation({
        onSuccess: (response, variables) => {
            // Mark validation as completed
            isValidationInProgressRef.current = false;

            // Update the previous item to be the current newItem for next validation
            previousItemRef.current = { ...variables.newItem };

            // Store the last validated values to prevent re-validation of same data
            lastValidatedValuesRef.current = { ...variables.newItem };

            // Mark that we've done initial template validation
            isInitialTemplateValidationRef.current = true;

            // Track validated values for fields that were validated
            if (variables.attributeNames && variables.attributeNames.length > 0) {
                const formValues = methods.getValues();
                variables.attributeNames.forEach(attributeName => {
                    const value = get(formValues, attributeName);
                    lastValidatedFieldValuesRef.current.set(attributeName, value === undefined ? null : value);
                });
            } else {
                // If validating all attributes, track all values
                const formValues = methods.getValues();
                Object.keys(variables.newItem).forEach(attributeName => {
                    if (attributeName !== 'category__') {
                        const value = get(formValues, attributeName);
                        lastValidatedFieldValuesRef.current.set(attributeName, value === undefined ? null : value);
                    }
                });
            }

            Object.keys(template || {}).forEach(attributeName => {
                if (attributeName !== 'category__') {
                    removeErrorByAttributeName(attributeName);
                    removeWarningByAttributeName(attributeName);
                }
            });

            // Add new errors and warnings from validation response
            if (response.validations) {
                Object.entries(response.validations).forEach(([attributeName, validations]) => {
                    validations.forEach(validation => {
                        if (validation.level === 'Error' || validation.level === 'Failure') {
                            addError(attributeName, {
                                ...validation,
                                attributeName: attributeName
                            });
                        } else if (validation.level === 'Warning') {
                            addWarning(attributeName, {
                                ...validation,
                                attributeName: attributeName
                            });
                        }
                    });
                });
            }
        },
        onError: (_error) => {
            isValidationInProgressRef.current = false;
        }
    });

    // Helper function to perform validation with proper checks
    const performValidation = useCallback((newItem: any, isInitialValidation = false, attributeNames?: string[]) => {
        // Skip if validation is already in progress
        if (isValidationInProgressRef.current) {
            return;
        }

        // Skip if this is the same data we just validated
        if (lastValidatedValuesRef.current &&
            JSON.stringify(newItem) === JSON.stringify(lastValidatedValuesRef.current)) {
            return;
        }

        // Skip if this is not the initial template validation and we haven't done initial validation yet
        if (!isInitialValidation && !isInitialTemplateValidationRef.current) {
            return;
        }

        isValidationInProgressRef.current = true;

        // Ensure category__ is always present with empty string
        const newItemWithCategory = {
            ...newItem,
            category__: ''
        };

        const oldItemWithCategory = {
            ...previousItemRef.current,
            category__: ''
        };

        validateItem({
            attributeNames: attributeNames || [],
            newItem: newItemWithCategory,
            oldItem: oldItemWithCategory
        });
    }, [validateItem]);

    const { mutate: createItem, isPending } = useCreateOrUpdateItem({
        onError: (error) => {
            if (error.response?.data?.message) {
                toast.error(t(error.response.data.message, error.response.data.parameters));
            } else {
                toast.error(t('SAVE_ERROR_MESSAGE'));
            }
        },
        onSuccess: (createdItem) => {
            toast.success(t('SAVE_SUCCESS_MESSAGE'));

            // Call the onItemCreated callback with the created item
            onItemCreated(createdItem as ReferenceItemOption);

            // Close the dialog
            onOpenChange(false);
            // Reset the form
            methods.reset();
        }
    });

    const {
        showDiscardModal,
        handleCancelDialog,
        handleDiscardChanges,
        handleCloseRequest,
        handleDiscardModalChange,
        handleCancelButtonClick,
    } = useDialogWithDirtyCheck({
        isDirty,
        onOpenChange,
        onDiscard: () => methods.reset(),
    });

    // Reset form when dialog closes (including X button)
    useEffect(() => {
        if (!isOpen) {
            // Reset the previous item reference when dialog closes
            previousItemRef.current = { attributeStates__: [] };
            isValidationInProgressRef.current = false;
            lastValidatedValuesRef.current = null;
            isInitialTemplateValidationRef.current = false;
            lastValidatedFieldValuesRef.current.clear();

            // Clear any pending validation timeout
            if (validationTimeoutRef.current) {
                clearTimeout(validationTimeoutRef.current);
                validationTimeoutRef.current = null;
            }
        }
    }, [isOpen]);

    // Set form values when dialog opens with template
    useEffect(() => {
        if (template && isOpen) {
            methods.reset(template);
        }
    }, [isOpen]); // Only depend on isOpen, not template

    // Validate template when it changes (initial validation only)
    useEffect(() => {
        if (template && isOpen && !isInitialTemplateValidationRef.current) {
            performValidation(template, true);
        }
    }, [template, isOpen, performValidation]);

    // Watch for form changes and validate (only after initial template validation)
    const watchedValues = methods.watch();
    useEffect(() => {
        if (isOpen && Object.keys(watchedValues).length > 0 && isInitialTemplateValidationRef.current) {
            // Clear any existing timeout
            if (validationTimeoutRef.current) {
                clearTimeout(validationTimeoutRef.current);
            }

            // Set a new debounced validation
            validationTimeoutRef.current = setTimeout(() => {
                performValidation(watchedValues, false);
            }, 1500); // Debounce validation calls

            return () => {
                if (validationTimeoutRef.current) {
                    clearTimeout(validationTimeoutRef.current);
                    validationTimeoutRef.current = null;
                }
            };
        }
    }, [watchedValues, isOpen, performValidation]);

    const onSubmit = useCallback((data: any) => {
        createItem({
            merge: true,
            unique: true,
            item: data
        });
    }, [attribute, createItem, toast, t]);

    return (
        <Dialog open={isOpen} onOpenChange={handleCancelDialog}>
            <FormProvider {...methods}>
                <ValidationProvider>
                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                        <DialogContent
                            variant={'small'}
                            isDirty={isDirty}
                            onDiscardChanges={handleDiscardChanges}
                            showDiscardModalExternal={showDiscardModal}
                            onDiscardModalChange={handleDiscardModalChange}
                        >
                            <DialogHeader>
                                <DialogHeaderDefault onClose={handleCloseRequest}>{t('CREATE_NEW_ITEM')}</DialogHeaderDefault>
                            </DialogHeader>
                            <DialogBody className="tw-flex tw-flex-col tw-gap-10 tw-overflow-auto tw-max-h-[70vh] tw-min-h-[300px] tw-w[600px]">
                                {template ?  (
                                    Object.keys(template).map((attributeName: string) => {
                                        if (attributeName !== 'category__') {
                                            const att = dataModel?.attribute(attributeName);
                                            return att && <AttributeComponent
                                                key={att?.name}
                                                attribute={att}
                                                fieldPath={`${att.name}`}
                                                direction={isMd ? 'horizontal' : 'vertical'}
                                                labelClassName="md:tw-w-36 !tw-w-28 block lg:tw-w-40 tw-whitespace-normal tw-break-words tw-hyphens-auto"
                                            />;
                                        }
                                        return null;
                                    })
                                ) : (
                                    <div className='tw-text-t-sm tw-text-t-primary tw-text-center tw-w-full'>{t('NO_ATTRIBUTES_FOUND')}</div>
                                )}
                            </DialogBody>
                            <DialogFooter>
                                <Button
                                    type="button"
                                    onClick={() => {
                                        const formData = methods.getValues();
                                        onSubmit(formData);
                                    }}
                                    disabled={isPending || !isDirty}
                                    loading={isPending}
                                >
                                    {t('CREATE_AND_CONTINUE')}
                                </Button>
                                <Button variant="secondary" onClick={handleCancelButtonClick} disabled={isPending}>
                                    {t('CANCEL')}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </form>
                </ValidationProvider>
            </FormProvider>
        </Dialog>
    );
};
